<template>
  <div class="modal fade" tabindex="-1" :id="id">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">New Device</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <div class="col-12 mb-3">
              <label for="factory" class="form-label">
                <span class="text-danger">*</span>
                Factory:
              </label>
              <select id="factory" v-model="factory" class="form-control">
                <option :value="null">Please select</option>
                <option v-for="device_factory in factories" :key="device_factory.ref" :value="device_factory.ref">
                  {{device_factory.ref + ' - ' + device_factory.name + ' (' + device_factory.region + ')'}}
                </option>
              </select>
              <span class="text-danger w-100" v-if="errors && errors.factory">{{errors.factory[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <label for="type" class="form-label">
                <span class="text-danger">*</span>
                Type:
              </label>
              <div class="btn-group btn-group-toggle w-100">
                <a class="btn border"
                  :class="{'active btn-secondary': type == 'gateway', 'btn-outline-secondary': type != 'gateway'}"
                  title="Gateway"
                  @click="type = 'gateway'; model = null; firmware = null;">
                  <i class="bi bi-wifi"></i>
                  <b> Gateway</b>
                </a>
                <a class="btn border"
                  :class="{'active btn-secondary': type == 'module', 'btn-outline-secondary': type != 'module'}"
                  title="Module"
                  @click="type = 'module'; model = null; firmware = null;">
                  <i class="bi bi-cpu"></i>
                  <b> Module</b>
                </a>
                <a class="btn border"
                  :class="{'active btn-secondary': type == 'sensor', 'btn-outline-secondary': type != 'sensor'}"
                  title="Sensor"
                  @click="type = 'sensor'; model = null; firmware = null;">
                  <i class="bi bi-thermometer-half"></i>
                  <b> Sensor</b>
                </a>
              </div>
              <span class="text-danger w-100" v-if="errors && errors.type">{{errors.type[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="model" class="form-label">
                  <span class="text-danger">*</span>
                  Model:
                </label>
                <div class="input-group">
                  <select id="model" v-model="model" class="form-control" @change="firmware_version=null">
                    <option :value="null">Please select</option>
                    <option
                      v-for="device_model in device_models" 
                      v-bind:key="device_model.ref"
                      :value="device_model.ref"
                      >
                      {{device_model.ref + ' - ' + device_model.name}}
                    </option>
                  </select>
                  <div class="input-group-text text-muted" v-if="model">v</div>
                  <input type="text" class="form-control" v-if="model" v-model="firmware_version" placeholder="1.0.0">
                </div>
              </div>
              <span class="text-danger w-100" v-if="errors && errors.model">{{errors.model[0]}}</span>
              <span class="text-danger w-100" v-if="errors && errors.firmware_version">{{errors.firmware_version[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="serial_number" class="form-label">Serial Number:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the device EUI." v-model="auto_serial_number" @click="serial_number=null">
                  </div>
                  <input type="text" class="form-control" v-model="serial_number" :disabled="auto_serial_number" :placeholder="auto_serial_number ? 'auto-generated' : 'enter device serial number'">
                </div>
              </div>
              <small class="text-secondary" v-if="auto_name">Will be set to the same value as the device EUI</small>
              <small class="text-secondary" v-else>Must be 16-32 characters from 0 to 9 and A to F</small>
              <span class="text-danger w-100" v-if="errors && errors.serial_number">{{errors.serial_number[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="eui" class="form-label">EUI:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the device EUI." v-model="auto_eui" @click="eui=null">
                  </div>
                  <input type="text" class="form-control" v-model="eui" :disabled="auto_eui" :placeholder="auto_eui ? 'auto-generated' : 'enter device eui'">
                </div>
              </div>
              <small class="text-secondary">Must be 16 characters from 0 to 9 and A to F</small>
              <span class="text-danger w-100" v-if="errors && errors.eui">{{errors.eui[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="name" class="form-label">Name:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="copy the device EUI." v-model="auto_name" @click="name=null">
                  </div>
                  <input type="text" class="form-control" v-model="name" :disabled="auto_name" :placeholder="auto_name ? 'auto-generated' : 'enter device name'">
                </div>
              </div>
              <small class="text-secondary" v-if="auto_name">Will be set to the same value as the device EUI</small>
              <span class="text-danger w-100" v-if="errors && errors.name">{{errors.name[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="name" class="form-label">Manufactured:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="copy the device EUI." v-model="auto_manufactured" @click="manufactured_week=null;manufactured_year=null">
                  </div>
                  <input type="number" class="form-control" v-model.number="manufactured_week" :disabled="auto_manufactured" :placeholder="auto_manufactured ? 'this week' : 'week (ww)'">
                  <input type="number" class="form-control" v-model.number="manufactured_year" :disabled="auto_manufactured" :placeholder="auto_manufactured ? 'this year' : 'year (yy)'">
                </div>
              </div>
              <span class="text-danger w-100" v-if="errors && errors.manufactured_week">{{errors.manufactured_week[0]}}</span>
              <span class="text-danger w-100" v-if="errors && errors.manufactured_year">{{errors.manufactured_year[0]}}</span>
            </div>
          </div>

          <div class="row" v-if="type=='module'">
            <div class="col-12 mb-3">
              <label for="activation" class="form-label">
                <span class="text-danger">*</span>
                Activation:
              </label>
              <div class="btn-group btn-group-toggle w-100">
                <a class="btn border"
                  :class="{'active btn-secondary': activation == 'ABP', 'btn-outline-secondary': activation != 'ABP'}"
                  title="ABP"
                  @click="activation = 'ABP'; model = null; firmware = null;">
                  <b>ABP</b>
                </a>
                <a class="btn border"
                  :class="{'active btn-secondary': activation == 'OTAA', 'btn-outline-secondary': activation != 'OTAA'}"
                  title="OTAA"
                  @click="activation = 'OTAA'; model = null; firmware = null;">
                  <b>OTAA</b>
                </a>
              </div>
              <span class="text-danger w-100" v-if="errors && errors.type">{{errors.type[0]}}</span>
            </div>
          </div>

          <div class="row" v-if="type=='module' && activation=='OTAA'">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="app_key" class="form-label">Application Key:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the app key." v-model="auto_app_key" @click="app_key=null">
                  </div>
                  <input type="text" class="form-control" v-model="app_key" :disabled="auto_app_key" :placeholder="auto_app_key ? 'auto-generated' : 'enter app key'">
                </div>
              </div>
              <small class="text-secondary">Must be 32 characters from 0 to 9 and A to F</small>
              <span class="text-danger w-100" v-if="errors && errors.app_key">{{errors.app_key[0]}}</span>
            </div>
          </div>

          <div class="row" v-if="type=='module' && activation=='ABP'">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="dev_address" class="form-label">Device Address:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the device address." v-model="auto_dev_address" @click="dev_address=null">
                  </div>
                  <input type="text" class="form-control" v-model="dev_address" :disabled="auto_dev_address" :placeholder="auto_dev_address ? 'auto-generated' : 'enter device address'">
                </div>
              </div>
              <small class="text-secondary">Must be 8 characters from 0 to 9 and A to F</small>
              <span class="text-danger w-100" v-if="errors && errors.dev_address">{{errors.dev_address[0]}}</span>
            </div>
          </div>

          <div class="row" v-if="type=='module' && activation=='ABP'">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="app_session_key" class="form-label">Application Session Key:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the app session key." v-model="auto_app_session_key" @click="app_session_key=null">
                  </div>
                  <input type="text" class="form-control" v-model="app_session_key" :disabled="auto_app_session_key" :placeholder="auto_app_session_key ? 'auto-generated' : 'enter app session key'">
                </div>
              </div>
              <small class="text-secondary">Must be 32 characters from 0 to 9 and A to F</small>
              <span class="text-danger w-100" v-if="errors && errors.app_session_key">{{errors.app_session_key[0]}}</span>
            </div>
          </div>

          <div class="row" v-if="type=='module' && activation=='ABP'">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="nwk_session_key" class="form-label">Network Session Key:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the network session key." v-model="auto_nwk_session_key" @click="nwk_session_key=null">
                  </div>
                  <input type="text" class="form-control" v-model="nwk_session_key" :disabled="auto_nwk_session_key" :placeholder="auto_nwk_session_key ? 'auto-generated' : 'enter network session key'">
                </div>
              </div>
              <small class="text-secondary">Must be 32 characters from 0 to 9 and A to F</small>
              <span class="text-danger w-100" v-if="errors && errors.nwk_session_key">{{errors.nwk_session_key[0]}}</span>
            </div>
          </div>

          <div class="row" v-if="type=='gateway'">
            <div class="col-12 mb-3">
              <div class="input-group">
                <label for="iccid" class="form-label">ICCID:</label>
                <div class="input-group">
                  <div class="input-group-text">
                    <input class="form-check-input mt-0" type="checkbox" aria-label="auto generate the ICCID." v-model="include_iccid" @click="iccid=null">
                  </div>
                  <input type="text" class="form-control" v-model="iccid" :disabled="!include_iccid" :placeholder="!include_iccid ? 'not applicable' : 'enter ICCID'">
                </div>
              </div>
              <small class="text-secondary">Must be 20 numeric characters</small>
              <span class="text-danger w-100" v-if="errors && errors.iccid">{{errors.iccid[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12 mb-3">
              <label for="name" class="form-label">Tags:</label>
              <div class="input-group">
                <vue-tags-input class="w-100"
                    v-model="tag"
                    :tags="tags"
                    @tags-changed="(newTags) => (tags = newTags)"
                  />
              </div>
              <span class="text-danger w-100" v-if="errors && errors.tags">{{errors.tags[0]}}</span>
            </div>
          </div>

          <div class="row">
            <div class="col-12">
              <button
                type="button"
                class="btn btn-primary w-100"
                @click="add_device"
              >
                Add device
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueTagsInput from "@sipec/vue3-tags-input";

import Storage from "../../helpers/storage";

export default {
  name: "NewDevice",
  props: ["id", "models", "factories"],
  components: { VueTagsInput },
  data() {
    return {
      type: null,
      serial_number: null,
      eui: null,
      name: null,
      manufactured_week: null,
      manufactured_year: null,
      model: null,
      firmware_version: null,
      activation: null,
      app_key: null,
      dev_address: null,
      app_session_key: null,
      nwk_session_key: null,
      iccid: null,
      factory: null,
      tags: [],

      auto_serial_number: true,
      auto_eui: true,
      auto_name:  true,
      auto_manufactured: true,
      auto_app_key: true,
      auto_dev_address: true,
      auto_app_session_key: true,
      auto_nwk_session_key: true,
      include_iccid: false,

      tag: "",
      errors: {}
    };
  },
  computed: {
    device_models: function () {
      let device_type = this.type;
      return this.models.filter(function (model) {
        return model.type == device_type;
      });
    },
  },
  methods: {
    add_device() {
      let app = this;

      var sessionTokens = Storage.get("session_tokens", true),
        axeTokens = Storage.get("tokens");

      let data = {
        type: this.type,
        serial_number: this.serial_number,
        eui: this.eui,
        name: this.name,
        manufactured_week: this.manufactured_week,
        manufactured_year: this.manufactured_year,
        model: this.model,
        firmware_version: this.firmware_version,
        activation: this.activation,
        app_key: this.app_key,
        dev_address: this.dev_address,
        app_session_key: this.app_session_key,
        nwk_session_key: this.nwk_session_key,
        iccid: this.iccid,
        factory: this.factory,
        tags: this.tags.map(function(tag) {
          return tag.text;
        }),
      };

      // remove all null keys
      Object.keys(data).forEach(key => {
        if (data[key] === null || (Array.isArray(data[key]) && data[key].length == 0)) {
          delete data[key];
        }
      });

      axios
        .post(
          process.env.MIX_APP_URL + "/api/v1/devices", data,
          {
            headers: {
              Authorization:
                sessionTokens.token_type + " " + sessionTokens.access_token,
              "RT-Authorization": axeTokens.access_token,
            },
          }
        )
        .then((response) => {
          this.emitter.emit("device_created", response.data);

          let newDeviceModal = bootstrap.Modal.getInstance(
            document.getElementById(this.id)
          );
          newDeviceModal.hide();

          this.emitter.emit("popup_alert", {
            type: "success",
            text: "Device was succesfuly created!",
          });
        })
        .catch(function (error) {
          if (error.response) {
            app.errors = error.response && error.response.data
            let errorMsg = "";

            for (let key in error.response.data) {
              errorMsg += error.response.data[key].join("");
            }

            app.emitter.emit("popup_alert", {
              type: "danger",
              text: errorMsg,
            });
          } else if (error.request) {
            console.log(error.request);
          } else {
            console.log("Error", error.message);
          }
        });
    },
  },
};
</script>